import { Box, ExclamationTriangleIcon, Flex, TabContent, Tabs, Text } from 'components'
import { startCase } from 'lodash-es'
import { useSearchParams } from 'react-router'
import { isLockedEvent, useEvent } from '../../utils'
import { useFeature } from '../../utils/featureFlags'
import EventAnalytics from './EventAnalytics'
import EventIntelligence from './EventIntelligence'
import EventLeads from './EventLeads'

export default function EventInfoTabs() {
  const { event } = useEvent()
  const [searchParams, setSearchParams] = useSearchParams()
  const enableIntelligence = useFeature('ai-summaries')
  const locked = isLockedEvent()

  if (!event) return
  const currentTab = (searchParams.get('view') || 'sessions') as Tab

  const makeContent = (tab: Tab): TabContent => ({
    label: startCase(tab),
    value: tab,
    labelProps: {
      textStyle: 'bodyMedium',
      fontWeight: 'normal',
      py: 3,
      onClick: () => setSearchParams({ view: tab }),
    },
    children:
      tab === 'sessions' ? (
        <EventLeads />
      ) : tab === 'intelligence' ? (
        <EventIntelligence />
      ) : (
        <EventAnalytics />
      ),
    px: { base: 2, md: 0 },
    mt: tab === 'analytics' ? 0 : 4,
    display: 'flex',
    overflowX: 'auto',
  })

  const tabData = [
    makeContent('sessions'),
    ...(enableIntelligence && event.insights?.autoGenResults ? [makeContent('intelligence')] : []),
    makeContent('analytics'),
  ]

  return (
    <Flex overflow="hidden" position="relative">
      <Tabs
        display="flex"
        flexDirection="column"
        overflow="auto"
        items={tabData}
        value={currentTab}
        lazyMount
        mt={0}
      />

      {locked && (
        <Box
          position="absolute"
          inset={0}
          background="#FFFFFF90"
          backdropFilter="blur(4px)"
          zIndex={1}
          borderRadius="md"
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={4}
        >
          <ExclamationTriangleIcon fill="danger.600" size="lg" />
          <Text textStyle="bodyMedium">
            Inactive Subscription. Please contact your workspace admin to view data collected at
            this event.
          </Text>
        </Box>
      )}
    </Flex>
  )
}

type Tab = (typeof tabs)[number]
const tabs = ['sessions', 'intelligence', 'analytics']
